import { useTranslation } from 'react-i18next';
import { ErrorState } from '@ui-kit';
import type { PermissionItem } from '../types/role';

interface PermissionMatrixTableProps {
  /** Permission 카탈로그 전체 목록 */
  catalog: PermissionItem[];
  /** 현재 역할에 부여된 Permission 코드 집합 */
  grantedCodes: Set<string>;
  /** 편집 중 변경 사항: 추가 예정 코드 집합 */
  pendingAdd: Set<string>;
  /** 편집 중 변경 사항: 제거 예정 코드 집합 */
  pendingRemove: Set<string>;
  /** 읽기 전용 모드 (시스템 역할 또는 ROLE_WRITE 미보유) */
  readonly?: boolean;
  /** 체크박스 토글 핸들러 */
  onToggle?: (code: string, currentChecked: boolean) => void;
  /**
   * 리소스 행 전체 토글 핸들러.
   * resource: 리소스 키(예: 'USER'), codes: 해당 리소스의 모든 권한 코드, allChecked: 현재 행 전체 체크 여부
   */
  onBulkToggle?: (resource: string, codes: string[], allChecked: boolean) => void;
  /** 저장 실패 오류 메시지 (있으면 ErrorState 표시) */
  saveError?: string;
  /** ErrorState 재시도 핸들러 */
  onSaveErrorRetry?: () => void;
}

/** resource → 한글 이름 매핑 */
const RESOURCE_LABEL: Record<string, string> = {
  USER: '사용자',
  ROLE: '역할',
  CODE: '공통코드',
  GATEWAY: '게이트웨이',
  AUDIT: '감사',
};

/** action → 한글 이름 매핑 */
const ACTION_LABEL: Record<string, string> = {
  READ: '조회',
  WRITE: '수정',
  ADMIN: '관리',
};

const ACTION_ORDER = ['READ', 'WRITE', 'ADMIN'];

/**
 * Permission 매트릭스 테이블.
 * - 행: resource (USER / ROLE / CODE / GATEWAY / AUDIT)
 * - 열: action (READ / WRITE / ADMIN)
 * - 각 셀: 체크박스 (granted=채워진 보라 / added=초록 하이라이트 / removed=빨간 취소선 / unchecked=비어있음)
 * - N/A 셀 (해당 resource+action 조합이 카탈로그에 없음): "—"
 * - 행 헤더: 일괄 토글 버튼 (편집 모드일 때만 표시)
 * - 저장 오류: ErrorState 컴포넌트로 표시
 */
export function PermissionMatrixTable({
  catalog,
  grantedCodes,
  pendingAdd,
  pendingRemove,
  readonly = false,
  onToggle,
  onBulkToggle,
  saveError,
  onSaveErrorRetry,
}: PermissionMatrixTableProps) {
  const { t } = useTranslation();

  // resource 목록 추출 (카탈로그 순서 유지, 고유값)
  const resources = Array.from(new Set(catalog.map((p) => p.resource)));

  // resource + action → permission 조회 맵
  const permMap = new Map<string, PermissionItem>();
  catalog.forEach((p) => permMap.set(`${p.resource}::${p.action}`, p));

  // 실제 존재하는 action 열 목록 (ACTION_ORDER 기반, 카탈로그에 있는 것만)
  const actions = ACTION_ORDER.filter((a) =>
    catalog.some((p) => p.action === a)
  );

  const getCellState = (code: string, isGranted: boolean) => {
    if (pendingAdd.has(code)) return 'added';
    if (pendingRemove.has(code)) return 'removed';
    if (isGranted) return 'checked';
    return 'unchecked';
  };

  /** 특정 리소스 행의 모든 권한 코드 목록 (카탈로그 존재 항목만) */
  const getResourceCodes = (resource: string): string[] =>
    catalog.filter((p) => p.resource === resource).map((p) => p.code);

  /**
   * 현재 행이 "모두 체크" 상태인지 판단.
   * pendingAdd/Remove 반영 후 코드 전체가 부여된 상태이면 true.
   */
  const isRowAllChecked = (resource: string): boolean => {
    const codes = getResourceCodes(resource);
    if (codes.length === 0) return false;
    return codes.every((code) => {
      const inPendingAdd = pendingAdd.has(code);
      const inPendingRemove = pendingRemove.has(code);
      const isGranted = grantedCodes.has(code);
      if (inPendingRemove) return false;
      if (inPendingAdd) return true;
      return isGranted;
    });
  };

  const pendingAddCount = pendingAdd.size;
  const pendingRemoveCount = pendingRemove.size;
  const hasPendingChanges = pendingAddCount > 0 || pendingRemoveCount > 0;

  const showBulkToggle = !readonly && !!onBulkToggle;

  return (
    <div>
      {/* 저장 오류 ErrorState */}
      {saveError && (
        <div style={{ marginBottom: 'var(--spacing-3)' }}>
          <ErrorState
            title={t('role.matrix.saveError.title')}
            message={saveError}
            onRetry={onSaveErrorRetry}
          />
        </div>
      )}

      {/* 변경 사항 요약 배너 */}
      {hasPendingChanges && (
        <div
          role="status"
          aria-live="polite"
          style={{
            marginBottom: 'var(--spacing-3)',
            padding: 'var(--spacing-2) var(--spacing-3)',
            background: 'color-mix(in srgb, var(--color-brand-primary) 8%, var(--color-surface-muted))',
            border: '1px solid var(--color-brand-primary)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
            display: 'flex',
            gap: 'var(--spacing-3)',
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <span>{t('role.matrix.pendingBanner.label')}</span>
          {pendingAddCount > 0 && (
            <span style={{ color: 'var(--color-success)', fontWeight: 600 }}>
              +{pendingAddCount} {t('role.matrix.pendingBanner.added')}
            </span>
          )}
          {pendingRemoveCount > 0 && (
            <span style={{ color: 'var(--color-danger)', fontWeight: 600 }}>
              -{pendingRemoveCount} {t('role.matrix.pendingBanner.removed')}
            </span>
          )}
        </div>
      )}

      {/* 매트릭스 테이블 */}
      <div style={{ overflowX: 'auto' }}>
        <table
          role="grid"
          aria-label={t('role.matrix.ariaLabel')}
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 'var(--font-size-sm)',
            background: 'var(--color-surface-background)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
          }}
        >
          <thead>
            <tr>
              <th
                scope="col"
                style={{
                  padding: 'var(--spacing-3)',
                  background: 'var(--color-surface-elevated)',
                  color: 'var(--color-text-secondary)',
                  fontWeight: 700,
                  fontSize: 'var(--font-size-xs)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  textAlign: 'left',
                  minWidth: 110,
                  borderBottom: '1px solid var(--color-surface-border)',
                  borderRight: '1px solid var(--color-surface-border)',
                }}
              >
                {t('role.matrix.column.resource')}
              </th>
              {actions.map((action) => (
                <th
                  key={action}
                  scope="col"
                  style={{
                    padding: 'var(--spacing-3)',
                    background: 'var(--color-surface-elevated)',
                    color: 'var(--color-text-secondary)',
                    fontWeight: 700,
                    fontSize: 'var(--font-size-xs)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    textAlign: 'center',
                    borderBottom: '1px solid var(--color-surface-border)',
                    borderRight: '1px solid var(--color-surface-border)',
                  }}
                >
                  {ACTION_LABEL[action] ?? action}
                </th>
              ))}
              {/* 일괄 토글 열 헤더 — 편집 모드에서만 표시 */}
              {showBulkToggle && (
                <th
                  scope="col"
                  style={{
                    padding: 'var(--spacing-3)',
                    background: 'var(--color-surface-elevated)',
                    color: 'var(--color-text-secondary)',
                    fontWeight: 700,
                    fontSize: 'var(--font-size-xs)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    textAlign: 'center',
                    borderBottom: '1px solid var(--color-surface-border)',
                    minWidth: 70,
                  }}
                >
                  {t('role.matrix.column.bulkToggle')}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {resources.map((resource, rIdx) => {
              const rowAllChecked = isRowAllChecked(resource);
              const resourceCodes = getResourceCodes(resource);

              return (
                <tr key={resource}>
                  {/* 리소스 행 헤더 */}
                  <th
                    scope="row"
                    style={{
                      padding: 'var(--spacing-3)',
                      textAlign: 'left',
                      fontWeight: 700,
                      color: 'var(--color-text-primary)',
                      fontFamily: 'var(--font-family-mono)',
                      background: 'var(--color-surface-muted)',
                      borderBottom: rIdx < resources.length - 1 ? '1px solid var(--color-surface-border)' : 'none',
                      borderRight: '1px solid var(--color-surface-border)',
                      fontSize: 'var(--font-size-xs)',
                    }}
                  >
                    <div>{resource}</div>
                    <div style={{ fontFamily: 'var(--font-family-sans)', fontWeight: 400, color: 'var(--color-text-muted)', fontSize: '10px', marginTop: 2 }}>
                      {RESOURCE_LABEL[resource] ?? resource}
                    </div>
                  </th>
                  {/* 셀 */}
                  {actions.map((action) => {
                    const perm = permMap.get(`${resource}::${action}`);
                    const isNA = !perm;
                    const isGranted = perm ? grantedCodes.has(perm.code) : false;
                    const state = perm ? getCellState(perm.code, isGranted) : 'na';
                    const isChecked = state === 'checked' || state === 'added';
                    const isDirty = state === 'added' || state === 'removed';

                    return (
                      <td
                        key={action}
                        style={{
                          padding: 'var(--spacing-3)',
                          textAlign: 'center',
                          verticalAlign: 'middle',
                          borderBottom: rIdx < resources.length - 1 ? '1px solid var(--color-surface-border)' : 'none',
                          borderRight: '1px solid var(--color-surface-border)',
                          background: isNA
                            ? 'color-mix(in srgb, var(--color-surface-muted) 50%, transparent)'
                            : isDirty
                            ? 'color-mix(in srgb, var(--color-brand-primary) 4%, transparent)'
                            : undefined,
                        }}
                      >
                        {isNA ? (
                          <span
                            aria-label={t('role.matrix.cell.na')}
                            style={{
                              color: 'var(--color-text-muted)',
                              fontFamily: 'var(--font-family-mono)',
                              fontSize: 'var(--font-size-xs)',
                            }}
                          >
                            —
                          </span>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <button
                              type="button"
                              role="checkbox"
                              aria-checked={isChecked}
                              aria-label={`${perm!.nameKo} (${perm!.code})`}
                              disabled={readonly && !onToggle}
                              onClick={() => onToggle?.(perm!.code, isChecked)}
                              title={perm!.nameKo}
                              style={{
                                width: 28,
                                height: 28,
                                border: `2px solid ${
                                  state === 'checked'
                                    ? 'var(--color-brand-primary)'
                                    : state === 'added'
                                    ? 'var(--color-success)'
                                    : state === 'removed'
                                    ? 'var(--color-danger)'
                                    : 'var(--color-surface-border)'
                                }`,
                                borderRadius: 'var(--radius-sm)',
                                background:
                                  state === 'checked'
                                    ? 'var(--color-brand-primary)'
                                    : state === 'added'
                                    ? 'var(--color-success)'
                                    : 'var(--color-surface-elevated)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: onToggle ? 'pointer' : 'not-allowed',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 700,
                                color:
                                  state === 'checked' || state === 'added'
                                    ? 'var(--color-text-inverse)'
                                    : state === 'removed'
                                    ? 'var(--color-danger)'
                                    : 'transparent',
                                transition: 'background 0.15s, border-color 0.15s',
                                opacity: readonly && !onToggle ? 0.55 : 1,
                                textDecoration: state === 'removed' ? 'line-through' : 'none',
                              }}
                            >
                              {(state === 'checked' || state === 'added') ? '✓' : state === 'removed' ? '✕' : ''}
                            </button>
                            <span
                              style={{
                                fontFamily: 'var(--font-family-mono)',
                                fontSize: '10px',
                                color: isDirty ? 'var(--color-brand-primary)' : 'var(--color-text-muted)',
                                fontWeight: isDirty ? 700 : 400,
                              }}
                            >
                              {perm!.code.split('_')[1]}
                            </span>
                          </div>
                        )}
                      </td>
                    );
                  })}

                  {/* 일괄 토글 셀 */}
                  {showBulkToggle && (
                    <td
                      style={{
                        padding: 'var(--spacing-3)',
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        borderBottom: rIdx < resources.length - 1 ? '1px solid var(--color-surface-border)' : 'none',
                      }}
                    >
                      <button
                        type="button"
                        aria-label={
                          rowAllChecked
                            ? t('role.matrix.bulkToggle.unselectAll', { resource })
                            : t('role.matrix.bulkToggle.selectAll', { resource })
                        }
                        title={
                          rowAllChecked
                            ? t('role.matrix.bulkToggle.unselectAll', { resource })
                            : t('role.matrix.bulkToggle.selectAll', { resource })
                        }
                        onClick={() => onBulkToggle!(resource, resourceCodes, rowAllChecked)}
                        style={{
                          padding: '2px var(--spacing-2)',
                          fontSize: '10px',
                          fontWeight: 600,
                          background: rowAllChecked
                            ? 'color-mix(in srgb, var(--color-danger) 10%, var(--color-surface-elevated))'
                            : 'color-mix(in srgb, var(--color-success) 10%, var(--color-surface-elevated))',
                          border: `1px solid ${rowAllChecked ? 'var(--color-danger)' : 'var(--color-success)'}`,
                          borderRadius: 'var(--radius-sm)',
                          color: rowAllChecked ? 'var(--color-danger)' : 'var(--color-success)',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          fontFamily: 'var(--font-family-sans)',
                        }}
                      >
                        {rowAllChecked
                          ? t('role.matrix.bulkToggle.unselectLabel')
                          : t('role.matrix.bulkToggle.selectLabel')}
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 범례 */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--spacing-3)',
          padding: 'var(--spacing-3)',
          background: 'var(--color-surface-elevated)',
          borderRadius: 'var(--radius-sm)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
          marginTop: 'var(--spacing-3)',
        }}
      >
        {[
          {
            label: t('role.matrix.legend.granted'),
            bg: 'var(--color-brand-primary)',
            border: 'var(--color-brand-primary)',
          },
          {
            label: t('role.matrix.legend.added'),
            bg: 'var(--color-success)',
            border: 'var(--color-success)',
          },
          {
            label: t('role.matrix.legend.removed'),
            bg: 'var(--color-surface-elevated)',
            border: 'var(--color-danger)',
          },
          {
            label: t('role.matrix.legend.unchecked'),
            bg: 'var(--color-surface-elevated)',
            border: 'var(--color-surface-border)',
          },
        ].map(({ label, bg, border }) => (
          <span
            key={label}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)' }}
          >
            <span
              style={{
                width: 14,
                height: 14,
                borderRadius: 'var(--radius-sm)',
                border: `2px solid ${border}`,
                background: bg,
                display: 'inline-block',
                flexShrink: 0,
              }}
            />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}
